import React, { useEffect, useState } from 'react';

// Microsoft-family consumer mail domains. We show a deliverability notice when
// users type these during the IP-warmup period — Outlook.com applies tight
// caching/throttling on new sender IPs and our mail can be delayed for hours
// even with full SPF/DKIM/DMARC alignment.
const MS_DOMAINS = new Set([
  'hotmail.com', 'hotmail.co.uk', 'hotmail.fr', 'hotmail.it', 'hotmail.de', 'hotmail.es', 'hotmail.ca',
  'outlook.com', 'outlook.co.uk', 'outlook.fr', 'outlook.it', 'outlook.de', 'outlook.es', 'outlook.com.au',
  'live.com',    'live.ca',       'live.co.uk',
  'msn.com',
]);

function looksLikeMicrosoft(email) {
  const at = String(email || '').lastIndexOf('@');
  if (at < 0) return false;
  const domain = email.slice(at + 1).toLowerCase().trim();
  return MS_DOMAINS.has(domain);
}

// Module-level cache so every form on the page doesn't independently fetch the
// flag. Single in-flight promise dedupes concurrent first-render calls.
let _enabledCache = null;       // null=unknown, true/false=resolved
let _enabledPromise = null;
const FLAG_KEY = 'email.hotmail_warning';

async function checkFlag() {
  if (_enabledCache !== null) return _enabledCache;
  if (_enabledPromise) return _enabledPromise;
  _enabledPromise = (async () => {
    try {
      // Public feature-flag map endpoint. Already honors rollout_pct and
      // target_user_ids per-user. Falls back to env var if API is unreachable.
      const r = await fetch('/api/feature-flags/public');
      if (r.ok) {
        const map = await r.json();
        _enabledCache = !!map[FLAG_KEY];
        return _enabledCache;
      }
    } catch (_) {}
    // API unreachable → fall back to env var
    const v = process.env.REACT_APP_DELIVERY_WARMUP_NOTICE;
    _enabledCache = (v === '1' || v === 'true');
    return _enabledCache;
  })();
  try { return await _enabledPromise; }
  finally { _enabledPromise = null; }
}

/**
 * Inline notice shown only when the email value looks Microsoft-family AND
 * the `email.hotmail_warning` feature flag is enabled. Toggle in admin →
 * Feature Flags. Renders nothing otherwise.
 *
 *   <HotmailDeliveryNotice email={emailValue} />
 */
export default function HotmailDeliveryNotice({ email, style }) {
  const [enabled, setEnabled] = useState(_enabledCache);

  useEffect(() => {
    if (_enabledCache !== null) { setEnabled(_enabledCache); return; }
    let alive = true;
    checkFlag().then(v => { if (alive) setEnabled(v); });
    return () => { alive = false; };
  }, []);

  if (enabled === null || enabled === false) return null;
  if (!looksLikeMicrosoft(email)) return null;
  return (
    <div
      role="note"
      aria-live="polite"
      style={{
        marginTop: 6,
        padding: '8px 12px',
        background: '#fff8e1',
        color: '#856404',
        border: '1px solid #ffe082',
        borderRadius: 4,
        fontSize: 13,
        lineHeight: 1.45,
        ...style,
      }}
    >
      Heads up: emails to Hotmail, Outlook, Live, and MSN addresses may be delayed
      or filtered while we build sender reputation on our new mail server. If you
      don't see our mail within a few hours, please check your <strong>Junk</strong> folder
      (and mark it as not junk) — or use a different address such as Gmail or Yahoo
      to receive faster.
    </div>
  );
}

export { looksLikeMicrosoft };
